import { createSelector } from '@reduxjs/toolkit'
import { readRmsDomains } from '@racepointenergy/swc/scs/read-rms-domains'

import type { RootState } from 'app/store'
import type { DomainType } from 'features/domains/types'
import type { DomainUser } from 'features/team/types'
import {
  currentRootDomainIdSelector,
  rootDomainByIdSelector,
  rootDomainsDataSelector,
} from 'features/rootDomains/selectors'
import {
  checkUserIsAdmin,
  checkUserIsAdminSomewhere,
  checkUserIsDomainAdmin,
  getDomainsWhereUserIsAdmin,
  getUserDataFromDomain,
} from './utils'

export const userTopDomainsSelector = createSelector(
  (state: RootState) =>
    readRmsDomains.select({
      rootdomainid: currentRootDomainIdSelector(state) || undefined,
      users: 'self',
      depth: 0,
      jobs: true,
    })(state),
  (userTopDomainsResult) => (userTopDomainsResult.data || []) as DomainType[]
)

export const userHasManyTopDomainsSelector = createSelector(
  userTopDomainsSelector,
  (userTopDomains) => userTopDomains?.length > 1
)

export const userTopDomainsWithNestingSelector = createSelector(
  (state: RootState) =>
    readRmsDomains.select({
      rootdomainid: currentRootDomainIdSelector(state) || undefined,
      users: 'self',
      jobs: true,
    })(state),
  (userTopDomainsResult) => (userTopDomainsResult.data || []) as DomainType[]
)

// returns user data with permissions from his top sibling domains
export const userTopDomainsDataSelector = createSelector(
  userTopDomainsSelector,
  (userTopDomains) =>
    userTopDomains?.map((domain: DomainType) =>
      getUserDataFromDomain(domain)
    ) as DomainUser[]
)

export const domainIsRootSelector = createSelector(
  userTopDomainsSelector,
  currentRootDomainIdSelector,
  (userTopDomains, currentRootDomainId) =>
    userTopDomains?.some(
      (domain: DomainType) => domain.root && domain.id === currentRootDomainId
    )
)

export const domainNestingEnabledSelector = createSelector(
  [currentRootDomainIdSelector, rootDomainsDataSelector.data],
  (currentRootDomainId, userRootDomains) =>
    userRootDomains
      ?.filter((domain: DomainType) => domain.maxAllowedDepth > 1)
      .some((domain: DomainType) => domain.id === currentRootDomainId) || false
)

export const userAnyTopDomainIdSelector = createSelector(
  userTopDomainsDataSelector,
  (userTopDomains) => userTopDomains?.[0]?.domainId
)

export const isRootAdminSelector = createSelector(
  userTopDomainsDataSelector,
  domainIsRootSelector,
  (userTopDomainsData, domainIsRoot) =>
    userTopDomainsData
      ?.map((userData) => checkUserIsAdmin(userData))
      .some((isAdmin) => isAdmin) && domainIsRoot
)

export const isDomainAdminSelector = createSelector(
  [
    userTopDomainsWithNestingSelector,
    (state: RootState, domainId: string | undefined) => domainId,
  ],
  (userTopDomainsWithNesting, domainId) =>
    checkUserIsDomainAdmin(domainId, userTopDomainsWithNesting)
)

export const isDomainAdminSomewhereSelector = createSelector(
  userTopDomainsWithNestingSelector,
  (userTopDomainsWithNesting) =>
    checkUserIsAdminSomewhere(userTopDomainsWithNesting)
)

// userTopDomainsData?.[0] = because purchase permission is set on a root level and is the same for all subdomains
export const hasSubscriptionPermissionSelector = createSelector(
  userTopDomainsDataSelector,
  (userTopDomainsData) => userTopDomainsData?.[0]?.purchase
)

export const getDomainsWhereUserIsAdminSelector = createSelector(
  [
    isRootAdminSelector,
    currentRootDomainIdSelector,
    userTopDomainsSelector,
    userTopDomainsWithNestingSelector,
  ],
  (
    isRootAdmin,
    currentRootDomainId,
    userTopDomainsSelector,
    userTopDomainsWithNesting
  ) => {
    if (isRootAdmin) {
      return [
        userTopDomainsSelector.find(
          (domain) => domain.id === currentRootDomainId
        ),
      ]
    }

    return getDomainsWhereUserIsAdmin(userTopDomainsWithNesting)
  }
)

export const isNonDirectDealerSelector = (domainId: string | null) => {
  return createSelector(
    rootDomainByIdSelector(domainId),
    (rootDomain: DomainType | undefined) => {
      return (
        !!rootDomain?.vadDomainId || !!rootDomain?.distributorDomainIds.length
      )
    }
  )
}
